/* eslint-disable @typescript-eslint/camelcase */
import { Grid, TextField, makeStyles } from '@material-ui/core';
import { AccessAlarm, Subject } from '@material-ui/icons';
import DatePicker from 'components/DatePicker';
import ToggleCard from 'components/ToggleCard';
import i18n from 'i18n';
import React from 'react';
import PersonAddDisabledIcon from '@material-ui/icons/PersonAddDisabled';
import clsx from 'clsx';
import {
  CompanyData,
  PermissionsData,
  RolesEnum,
} from 'modules/authenticate/interfaces';
import GenericForm from 'skyone-lib/lib/GenericForm';
import { inAdminView, inSuperAdminView, inOperatorView } from 'utils/role';
import { format } from 'date-fns';
import styles from '../styles';

const useStyles = makeStyles(styles);

export interface RemoveSessionsFormDisplay {
  active: boolean;
  minimun_session_age: number | null;
  run_time: string;
  message: string;
}

export const removeSessionsInitialValues = {
  active: false,
  minimun_session_age: 6,
  run_time: '',
  message: '',
};

export interface Props {
  defaultValue: RemoveSessionsFormDisplay | null;
  companySelected: CompanyData | null;
  role?: RolesEnum;
  permissions: PermissionsData | undefined;
  onChange: (values: RemoveSessionsFormDisplay) => void;
  setHasError: React.Dispatch<React.SetStateAction<boolean>>;
}

const RemoveSessionsForm = ({
  defaultValue,
  role,
  companySelected,
  permissions,
  onChange,
  setHasError,
}: Props) => {
  const classes = useStyles();
  const [values, setValues] = React.useState<RemoveSessionsFormDisplay>(
    removeSessionsInitialValues
  );

  const handleChange = (values: RemoveSessionsFormDisplay, toggle: boolean) => {
    const newValues = { ...values, active: toggle };
    setValues(newValues);
    onChange(newValues);
  };

  React.useEffect(() => {
    if (defaultValue) {
      setValues(defaultValue);
    }
  }, [defaultValue]);

  React.useEffect(() => {
    setHasError(!!values?.active && values?.run_time === 'Invalid Date');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  return (
    <ToggleCard
      active={values.active}
      id="toggle-remove-session"
      disable={
        !!permissions &&
        !permissions?.edit_env_auto_remove_user_session_maintenance
      }
      title={i18n.t('REMOVE_USER_SESSION_KEY')}
      getActive={(value: boolean) => handleChange(values, !value)}
      icon={
        <PersonAddDisabledIcon
          className={clsx({
            [classes.iconGreen]: inAdminView(role, companySelected),
            [classes.iconGray]: inSuperAdminView(role, companySelected),
            [classes.iconBlue]: inOperatorView(role, companySelected),
            [classes.iconYellow]: companySelected,
          })}
        />
      }
    >
      <Grid container className={classes.FormContainer}>
        <AccessAlarm className={classes.iconMaintenance} />
        <Grid item className={classes.datePicker}>
          <DatePicker
            id="run-time"
            disabled={
              !!permissions &&
              !permissions?.edit_env_auto_remove_user_session_maintenance
            }
            type="time"
            label={i18n.t('AUTO_BLOCK_INSTANCES_TIME_KEY')}
            value={
              values?.run_time ? `Wed Nov 22 2023 ${values?.run_time}` : null
            }
            error={values?.run_time === 'Invalid Date'}
            keyValue="run_time"
            setFieldsValue={(key, value) => {
              if (
                typeof value === 'object' &&
                String(value) !== 'Invalid Date' &&
                value !== null
              ) {
                const date = new Date(String(value));

                const formattedDate = format(date, 'HH:mm:ss');
                handleChange(
                  { ...values, [key]: formattedDate },
                  values.active
                );
              }
              if (String(value) === 'Invalid Date' || value === null) {
                handleChange(
                  { ...values, [key]: 'Invalid Date' },
                  values.active
                );
              }
            }}
          />
        </Grid>
        <Grid container className={classes.datePicker}>
          <TextField
            type="number"
            disabled={
              !!permissions &&
              !permissions?.edit_env_auto_remove_user_session_maintenance
            }
            id="port_field"
            onKeyDown={evt =>
              ['e', 'E', '+', '-'].includes(evt.key) && evt.preventDefault()
            }
            label={i18n.t('MINIMUM_SESSION_AGE_KEY')}
            variant="outlined"
            value={
              values?.minimun_session_age ? values?.minimun_session_age : 0
            }
            onChange={({ target }) => {
              const value = Number(target.value);

              handleChange(
                { ...values, minimun_session_age: value },
                values.active
              );
            }}
            InputProps={{
              inputProps: {
                min: 0,
              },
            }}
            fullWidth
          />
        </Grid>
        <Grid item className={classes.textFieldContainer}>
          <Subject className={classes.subject} />
          <GenericForm
            errors={[]}
            fields={values}
            setFieldValue={(key, value) => {
              handleChange({ ...values, [key]: value }, values.active);
            }}
            screenFields={[
              {
                gridXs: 12,
                label: i18n.t('ADD_MESSAGE_REMOVE_SESSION_KEY'),
                stateKey: 'message',
                validateOnChange: true,
                disabled:
                  !!permissions &&
                  !permissions?.edit_env_auto_remove_user_session_maintenance,
              },
            ]}
          />
        </Grid>
      </Grid>
    </ToggleCard>
  );
};

export default RemoveSessionsForm;
